import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { environment } from '@hakimo-ui/hakimo/environments';
import { DataVersionOverride } from '@hakimo-ui/hakimo/types';

const storedDataVersionOverrideAtom = atomWithStorage<DataVersionOverride>(
  'locationAlarms/dataVersionOverride',
  'v2',
  undefined,
  { getOnInit: true }
);

/**
 * Which dataset the v2/v3 read surfaces use. Defaults to `v2` (legacy), and is
 * persisted, so a choice survives reloads.
 *
 * Driven by `DataVersionToggle`, which is shown to every user on the alarms
 * and incidents lists while the cloud-unification rollout is being validated.
 * It can still be set by hand for a surface that has no toggle:
 *
 *   localStorage.setItem('locationAlarms/dataVersionOverride', '"v3"')
 *
 * (the stored value is JSON, so the quotes are required), then reload. Set it
 * back to `'"v2"'` to return to legacy reads.
 *
 * When the deployment sets `FORCE_DATA_VERSION_V3`
 * (`environment.forceDataVersionV3`) this is always `v3`, whatever is stored,
 * and writes are ignored — the toggle is hidden there, so a stale stored `v2`
 * would otherwise leave the user on legacy data with no way back. The stored
 * choice is left untouched, so turning the flag off restores it.
 *
 * Read it through `useApiDataVersion()` when the value is destined for a
 * request — that hook applies the "only send v3" rule. Reading it raw suits
 * non-request uses, such as resetting dataset-specific UI state when it flips.
 */
export const dataVersionOverrideAtom = atom(
  (get): DataVersionOverride =>
    environment.forceDataVersionV3 ? 'v3' : get(storedDataVersionOverrideAtom),
  (_get, set, next: DataVersionOverride) => {
    if (!environment.forceDataVersionV3) {
      set(storedDataVersionOverrideAtom, next);
    }
  }
);
